import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Scrollfunctions from '../Components/Scrollfunctions';
import AOS from 'aos';
import 'aos/dist/aos.css';

// ImageLooper component remains the same, no changes needed
const ImageLooper = ({ images, title }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [hovering, setHovering] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    let intervalId;

    if (isMobile || hovering) {
      intervalId = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
      }, 1400);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [hovering, isMobile, images.length]);

  return (
    <div
      className="relative w-full pb-[100%] overflow-hidden rounded-xl"
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      {images.map((imageSrc, index) => (
        <img
          key={imageSrc}
          src={imageSrc}
          alt={`${title} ${index + 1}`}
          className={`absolute inset-0 w-full h-full object-cover rounded-xl transition-all duration-500 ease-in-out`}
          style={{
            opacity: index === currentIndex ? 1 : 0,
            transform: `scale(${index === currentIndex ? 1 : 1.05})`,
            zIndex: index === currentIndex ? 1 : 0,
          }}
        />
      ))}
    </div>
  );
};

const Thirdslide = () => {
  const [isMobile, setIsMobile] = useState(false);
  const scrollContainerRef = useRef(null);
  const userInteractedRef = useRef(false);
  const autoScrollTimerRef = useRef(null);
  const userInteractionTimeoutRef = useRef(null);

  // Initialize AOS and handle resize
  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      easing: 'ease-out',
    });

    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Function to start or restart the auto-scroll timer
  const startAutoScrollTimer = useCallback(() => {
    if (autoScrollTimerRef.current) {
      clearTimeout(autoScrollTimerRef.current);
    }

    autoScrollTimerRef.current = setTimeout(() => {
      if (userInteractedRef.current) {
        startAutoScrollTimer();
        return;
      }

      const container = scrollContainerRef.current;
      if (container) {
        const firstCard = container.querySelector('.third-slide-card');
        if (firstCard) {
          const cardWidth = firstCard.getBoundingClientRect().width;
          const gap = 24; // Corresponds to gap-6
          const scrollAmount = cardWidth + gap;
          const currentScrollLeft = container.scrollLeft;
          const scrollWidth = container.scrollWidth;
          const clientWidth = container.clientWidth;

          let targetScrollLeft = currentScrollLeft + scrollAmount;

          if (currentScrollLeft + clientWidth >= scrollWidth - 5) {
            targetScrollLeft = 0;
          }

          container.scrollTo({
            left: targetScrollLeft,
            behavior: 'smooth',
          });
        }
      }
      startAutoScrollTimer();
    }, 5000);
  }, []);

  // Function to handle any user interaction
  const handleUserInteraction = useCallback(() => {
    userInteractedRef.current = true;

    if (userInteractionTimeoutRef.current) {
      clearTimeout(userInteractionTimeoutRef.current);
    }
    if (autoScrollTimerRef.current) {
      clearTimeout(autoScrollTimerRef.current);
    }

    userInteractionTimeoutRef.current = setTimeout(() => {
      userInteractedRef.current = false;
      if (isMobile) {
        startAutoScrollTimer();
      }
    }, 3000);
  }, [isMobile, startAutoScrollTimer]);

  // Effect for setting up and tearing down event listeners
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener('touchstart', handleUserInteraction, { passive: true });
    container.addEventListener('wheel', handleUserInteraction, { passive: true });
    container.addEventListener('scroll', handleUserInteraction, { passive: true });
    container.addEventListener('touchend', handleUserInteraction, { passive: true });
    container.addEventListener('mouseup', handleUserInteraction, { passive: true });

    if (isMobile && !userInteractedRef.current) {
      startAutoScrollTimer();
    } else {
      if (autoScrollTimerRef.current) {
        clearTimeout(autoScrollTimerRef.current);
      }
    }

    return () => {
      if (autoScrollTimerRef.current) {
        clearTimeout(autoScrollTimerRef.current);
      }
      if (userInteractionTimeoutRef.current) {
        clearTimeout(userInteractionTimeoutRef.current);
      }
      if (container) {
        container.removeEventListener('touchstart', handleUserInteraction);
        container.removeEventListener('wheel', handleUserInteraction);
        container.removeEventListener('scroll', handleUserInteraction);
        container.removeEventListener('touchend', handleUserInteraction);
        container.removeEventListener('mouseup', handleUserInteraction);
      }
    };
  }, [isMobile, handleUserInteraction, startAutoScrollTimer]);

  // Scroll functions to move the container left and right
  const scrollLeft = () => {
    const container = scrollContainerRef.current;
    if (container) {
      const firstCard = container.querySelector('.third-slide-card');
      if (firstCard) {
        const cardWidth = firstCard.getBoundingClientRect().width;
        const gap = 24; // Corresponds to gap-6
        const scrollAmount = cardWidth + gap;
        container.scrollBy({
          left: -scrollAmount,
          behavior: 'smooth',
        });
        handleUserInteraction();
      }
    }
  };

  const scrollRight = () => {
    const container = scrollContainerRef.current;
    if (container) {
      const firstCard = container.querySelector('.third-slide-card');
      if (firstCard) {
        const cardWidth = firstCard.getBoundingClientRect().width;
        const gap = 24; // Corresponds to gap-6
        const scrollAmount = cardWidth + gap;
        container.scrollBy({
          left: scrollAmount,
          behavior: 'smooth',
        });
        handleUserInteraction();
      }
    }
  };

  const wallcoveringCategories = [
    {
      title: 'Decorative Wallpapers',
      desc: 'Beautiful patterns for every space.',
      Img: [
        'https://vizaura.s3.eu-north-1.amazonaws.com/Components/WallcoveringsThirdSlide/Wallcoveringsdecorativemain/1.jpg',
        'https://vizaura.s3.eu-north-1.amazonaws.com/Components/WallcoveringsThirdSlide/Wallcoveringsdecorativemain/2.jpg',
        'https://vizaura.s3.eu-north-1.amazonaws.com/Components/WallcoveringsThirdSlide/Wallcoveringsdecorativemain/3.jpg',
        'https://vizaura.s3.eu-north-1.amazonaws.com/Components/WallcoveringsThirdSlide/Wallcoveringsdecorativemain/4.jpg',
        'https://vizaura.s3.eu-north-1.amazonaws.com/Components/WallcoveringsThirdSlide/Wallcoveringsdecorativemain/5.jpg',
      ],
      link: '/wallcoverings/decorative',
    },
    {
      title: 'Abstract Wallpapers',
      desc: 'Modern designs that make a statement.',
      Img: [
        'https://vizaura.s3.eu-north-1.amazonaws.com/Components/WallcoveringsThirdSlide/Wallcoveringsabstractmain/1.jpg',
        'https://vizaura.s3.eu-north-1.amazonaws.com/Components/WallcoveringsThirdSlide/Wallcoveringsabstractmain/2.jpg',
        'https://vizaura.s3.eu-north-1.amazonaws.com/Components/WallcoveringsThirdSlide/Wallcoveringsabstractmain/3.jpg',
        'https://vizaura.s3.eu-north-1.amazonaws.com/Components/WallcoveringsThirdSlide/Wallcoveringsabstractmain/4.jpg',
        'https://vizaura.s3.eu-north-1.amazonaws.com/Components/WallcoveringsThirdSlide/Wallcoveringsabstractmain/5.jpg',
      ],
      link: '/wallcoverings/abstract',
    },
    {
      title: 'Corporate Wallpapers',
      desc: 'Professional looks for office spaces.',
      Img: [
        'https://vizaura.s3.eu-north-1.amazonaws.com/Components/WallcoveringsThirdSlide/Wallcoveringscorporatemain/1.jpg',
        'https://vizaura.s3.eu-north-1.amazonaws.com/Components/WallcoveringsThirdSlide/Wallcoveringscorporatemain/2.jpg',
        'https://vizaura.s3.eu-north-1.amazonaws.com/Components/WallcoveringsThirdSlide/Wallcoveringscorporatemain/3.jpg',
        'https://vizaura.s3.eu-north-1.amazonaws.com/Components/WallcoveringsThirdSlide/Wallcoveringscorporatemain/4.jpg',
        'https://vizaura.s3.eu-north-1.amazonaws.com/Components/WallcoveringsThirdSlide/Wallcoveringscorporatemain/5.jpg',
      ],
      link: '/wallcoverings/corporate',
    },
    {
      title: 'Kids Wallpapers',
      desc: 'Playful designs for children\'s rooms.',
      Img: [
        'https://vizaura.s3.eu-north-1.amazonaws.com/WallCoverings/maincards/kids1.jpg',
        'https://vizaura.s3.eu-north-1.amazonaws.com/WallCoverings/maincards/kids2.jpg',
        'https://vizaura.s3.eu-north-1.amazonaws.com/WallCoverings/maincards/kids3.jpg',
        'https://vizaura.s3.eu-north-1.amazonaws.com/WallCoverings/maincards/kids4.jpg',
        'https://vizaura.s3.eu-north-1.amazonaws.com/WallCoverings/maincards/kids5.jpg',
      ],
      link: '/wallcoverings/kids',
    },
    {
      title: 'Minimal and Pattern Wallpapers',
      desc: 'Subtle elegance and modern harmony.',
      Img: [
        'https://vizaura.s3.eu-north-1.amazonaws.com/WallCoverings/Minimal%26Pattern/(1).jpg',
        'https://vizaura.s3.eu-north-1.amazonaws.com/WallCoverings/Minimal%26Pattern/(2).jpg',
        'https://vizaura.s3.eu-north-1.amazonaws.com/WallCoverings/Minimal%26Pattern/(3).jpg',
        'https://vizaura.s3.eu-north-1.amazonaws.com/WallCoverings/Minimal%26Pattern/(4).jpg',
        'https://vizaura.s3.eu-north-1.amazonaws.com/WallCoverings/Minimal%26Pattern/(5).jpg',
      ],
      link: '/wallcoverings/minimal and pattern',
    },
  ];

  return (
    <div id="news" className="py-7 pt-10 font-sans">
      <div className="absolute inset-0 bg-white -z-10" />

      {/* Title section */}
      <div className="text-center mb-12 group" data-aos="fade-up">
        <h1 className="relative inline-block font-serif text-5xl md:text-6xl text-gray-500 tracking-tight pb-5">
          Explore
          <br className="md:hidden" /> Wallpapers
          {/* Animated underline span */}
          <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-full h-1 bg-gradient-to-r from-gray-900 to-gray-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out rounded-full" />
        </h1>
      </div>

      {/* Scrollable container for art print categories */}
      <div className="relative max-w-7xl mx-auto">
        <div
          ref={scrollContainerRef}
          className="flex overflow-x-auto gap-4 sm:gap-6 mt-8 sm:mt-10 px-4 pb-4 no-scrollbar md:grid md:grid-flow-col md:auto-cols-[calc((100%/3.08)-16px)] lg:auto-cols-[calc((100%/3.1)-18px)]"
          data-aos="fade-up"
          data-aos-delay="200"
        >
          {wallcoveringCategories.map((it, index) => (
            <div
              key={index}
              className="w-[84vw] max-w-[345px] sm:w-[52vw] md:w-full flex-shrink-0 bg-white rounded-xl shadow-lg hover:shadow-2xl overflow-hidden transform transition-all duration-300 hover:-translate-y-2 flex flex-col relative third-slide-card"
              data-aos="fade-up"
              data-aos-delay={200 + index * 100}
            >
              <Link to={it.link} className="block w-full h-full flex flex-col">
                <div className="relative pb-[100%] w-full">
                  <div className="absolute inset-0">
                    <ImageLooper images={it.Img} title={it.title} />
                  </div>
                </div>
                <div className="p-4 sm:p-5 flex-grow text-center flex flex-col justify-between">
                  <div>
                    <h3 className="text-lg sm:text-xl font-serif text-gray-700 mb-1">{it.title}</h3>
                    <p className="text-gray-500 text-xs sm:text-sm">{it.desc}</p>
                  </div>
                  <div>
                    <button className="mt-3 sm:mt-4 bg-gray-800 text-white font-serif text-xs sm:text-sm px-4 py-1.5 sm:py-2 rounded-lg hover:bg-gray-900 transition-colors duration-300 shadow-sm">
                      Buy Now
                    </button>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>

        {/* Desktop Scroll Left Button - visible on medium and larger screens */}
        <button
          onClick={scrollLeft}
          className="absolute -left-16 top-1/2 -translate-y-1/2 bg-gray-800 text-white p-3 rounded-full shadow-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 z-10 hidden md:block opacity-70 hover:opacity-100 transition-opacity duration-300"
          aria-label="Scroll left"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Desktop Scroll Right Button - visible on medium and larger screens */}
        <button
          onClick={scrollRight}
          className="absolute -right-16 top-1/2 -translate-y-1/2 bg-gray-800 text-white p-3 rounded-full shadow-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 z-10 hidden md:block opacity-70 hover:opacity-100 transition-opacity duration-300"
          aria-label="Scroll right"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        {/* Mobile Scroll Left Button - visible only on small screens */}
        <button
          onClick={scrollLeft}
          className="absolute left-2 top-1/2 -translate-y-1/2 bg-gray-800 text-white p-2 rounded-full shadow-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 z-10 md:hidden opacity-70 hover:opacity-100 transition-opacity duration-300"
          aria-label="Scroll left"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Mobile Scroll Right Button - visible only on small screens */}
        <button
          onClick={scrollRight}
          className="absolute right-2 top-1/2 -translate-y-1/2 bg-gray-800 text-white p-2 rounded-full shadow-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 z-10 md:hidden opacity-70 hover:opacity-100 transition-opacity duration-300"
          aria-label="Scroll right"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
      <div>
        <Scrollfunctions />
      </div>
    </div>
  );
};

export default Thirdslide;